import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { TrustStrip } from "@/components/TrustStrip";
import { NewsletterBanner } from "@/components/NewsletterBanner";
import { Toaster } from "@/components/ui/sonner";
import { useCartSync } from "@/hooks/useCartSync";

const CompareDrawer = lazy(() =>
  import("@/components/CompareDrawer").then((m) => ({ default: m.CompareDrawer })),
);
const CookieBanner = lazy(() =>
  import("@/components/CookieBanner").then((m) => ({ default: m.CookieBanner })),
);
const WelcomeOfferPopup = lazy(() =>
  import("@/components/WelcomeOfferPopup").then((m) => ({ default: m.WelcomeOfferPopup })),
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="max-w-md text-center">
        <p className="text-xs uppercase tracking-luxe text-muted-foreground">
          404, Untraceable
        </p>
        <h1 className="mt-6 font-serif text-5xl font-light">
          The stone you seek is not here.
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Perhaps it has been claimed, or perhaps it never existed.
        </p>
        <Link
          to="/"
          className="mt-10 inline-block border-b border-foreground pb-1 text-xs uppercase tracking-luxe"
        >
          Return to the House
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-3xl font-light">A moment of pause.</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Something interrupted this page. Try again, or return home.
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="border-b border-foreground pb-1 text-xs uppercase tracking-luxe"
          >
            Try again
          </button>
          <a
            href="/"
            className="border-b border-hairline pb-1 text-xs uppercase tracking-luxe text-muted-foreground"
          >
            Home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "HOWDEN, Certified Lab-Grown Diamonds" },
      {
        name: "description",
        content:
          "HOWDEN curates certified lab-grown diamonds, offered loose by the carat. IGI and GIA graded, traceable, considered.",
      },
      { name: "author", content: "HOWDEN" },
      { property: "og:title", content: "HOWDEN, Certified Lab-Grown Diamonds" },
      {
        property: "og:description",
        content:
          "Certified lab-grown diamonds, offered loose by the carat. IGI and GIA graded, traceable, considered.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "HOWDEN, Certified Lab-Grown Diamonds" },
      { name: "twitter:description", content: "Certified lab-grown diamonds, offered loose by the carat. IGI and GIA graded, traceable, considered." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/26916542-4560-4cce-863d-ede97a556894/id-preview-4467ef90--783ebbf1-c36b-4d94-9945-41f0fe8db186.lovable.app-1778482789986.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/26916542-4560-4cce-863d-ede97a556894/id-preview-4467ef90--783ebbf1-c36b-4d94-9945-41f0fe8db186.lovable.app-1778482789986.png" },
      { name: "google-site-verification", content: "le0S_oEzbj_yKulNshfEbb1VhOE7x4c_k41K7GFSgj0" },
    ],
    links: [
      { rel: "preload", href: appCss, as: "style" },
      { rel: "stylesheet", href: appCss },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function AppLayout() {
  useCartSync();
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <TrustStrip />
      <main className="flex-1">
        <Outlet />
      </main>
      <NewsletterBanner />
      <SiteFooter />
      <Suspense fallback={null}>
        <CompareDrawer />
      </Suspense>
      <Toaster position="top-center" />
      <Suspense fallback={null}>
        <WelcomeOfferPopup />
      </Suspense>
      <Suspense fallback={null}>
        <CookieBanner />
      </Suspense>
    </div>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <AppLayout />
    </QueryClientProvider>
  );
}
